import React, {useEffect, useMemo, useRef, useState} from 'react';
import Modal from '../../Modal';
import {useAuth} from '../../../helpers/AuthContext';
import {CROP_PRESETS, cropGeometry, normalizeRect} from '../../../../../shared/mediaCrop.js';
import {createCropApi, cropMessage} from './cropApi';
import './ImageCropEditor.css';

export const cropPreviewUrl = file => `${file.cdnPath || file.cdnUrl}${(file.cdnPath || file.cdnUrl)?.includes('?') ? '&' : '?'}etag=${encodeURIComponent(file.etag || file.uuid)}`;

function centered(preset, width, height, zoom = 1) {
  const ratio = CROP_PRESETS[preset].ratio || width / height;
  const w = Math.min(1, ratio * height / width) / zoom;
  const h = Math.min(1, width / (ratio * height)) / zoom;
  return {x: (1 - w) / 2, y: (1 - h) / 2, w, h};
}

export default function ImageCropEditor({file, files, preset: fixedPreset, onCancel, onSave}) {
  const auth = useAuth();
  const api = useMemo(() => createCropApi(auth), [auth.fetch, auth.API_URL]);
  const [source, setSource] = useState(null);
  const [preset, setPreset] = useState(fixedPreset || 'free');
  const [recipe, setRecipe] = useState(null);
  const [size, setSize] = useState(null);
  const [rect, setRect] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const drag = useRef(null);
  const image = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setSize(null); setRect(null); setSource(null); setRecipe(null); setNotice('');
    const prepare = async () => {
      try {
        if (file.source !== 'mediaCrop') { if (active) setSource(file); return; }
        const result = await api.reopen(file.uuid);
        if (!active) return;
        if (result.crop?.v !== 1) throw new Error('This crop recipe cannot be reopened. Choose the original image.');
        const root = files.find(item => item.uuid === result.crop.sourceUuid);
        if (!root) throw new Error('The original is unavailable in this library. Refresh or choose another image.');
        setRecipe(result.stale ? null : result.crop);
        setPreset(fixedPreset || result.crop.preset);
        setNotice(result.stale ? 'The original changed or is unavailable. Framing starts centered; review it before saving.' : 'Reopened from the retained original. Saving creates another copy.');
        setSource(root);
      } catch (failure) { if (active) { setError(cropMessage(failure)); setLoading(false); } }
    };
    prepare();
    return () => { active = false; };
  }, [api, file, files, fixedPreset, attempt]);

  const reset = (nextPreset = preset) => {
    setZoom(1);
    if (size) setRect(centered(nextPreset, size.width, size.height));
  };
  const loaded = event => {
    const dimensions = {width: event.target.naturalWidth, height: event.target.naturalHeight};
    setSize(dimensions); setLoading(false); setError('');
    const initial = recipe && recipe.preset === preset ? recipe.rect : centered(preset, dimensions.width, dimensions.height);
    setRect(normalizeRect(initial));
    setZoom(centered(preset, dimensions.width, dimensions.height).w / initial.w);
  };
  const move = (key, value) => setRect(current => ({...current,
    [key]: Math.max(0, Math.min(1 - current[key === 'x' ? 'w' : 'h'], value))}));
  const save = async () => {
    setError(''); setSaving(true);
    try {
      cropGeometry(preset, rect, size.width, size.height);
      const result = await api.create(source.uuid, preset, normalizeRect(rect));
      onSave(result.data);
    } catch (failure) { setError(cropMessage(failure)); }
    finally { setSaving(false); }
  };
  return <Modal isOpen title="Crop a reusable copy" size="large" className="image-crop-editor"
    ariaLabelledBy="image-crop-title" onClose={saving ? undefined : onCancel}
    showCloseButton={!saving} closeOnEscape={!saving} closeOnOverlayClick={false}>
    <p>The original and existing copies stay intact. Drag the frame, or use the controls below.</p>
    {notice && <p role="status">{notice}</p>}
    {error && <p role="alert">{error}</p>}
    {loading && <p role="status">Loading original image…</p>}
    {!fixedPreset && <label>Crop shape <select disabled={saving || !size} value={preset} onChange={event => {
      setPreset(event.target.value); reset(event.target.value);
    }}>{Object.keys(CROP_PRESETS).map(key => <option key={key} value={key}>{key}</option>)}</select></label>}
    <p>Shape: {preset}{size ? ` · Original ${size.width} × ${size.height}` : ''}</p>
    {source && <div className="image-crop-stage" ref={image}>
      <img src={cropPreviewUrl(source)} alt="Original image for crop framing" onLoad={loaded}
        onError={() => {setLoading(false); setSize(null); setError('The original image could not load. Retry to reload it.');}} draggable="false" />
      {rect && size && <div className={`image-crop-frame ${preset === 'profile' ? 'image-crop-circle' : ''}`}
        style={{left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%`}}
        aria-hidden="true" onPointerDown={event => {
          if (saving) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = {x: event.clientX, y: event.clientY, rect, bounds: image.current.getBoundingClientRect()};
        }} onPointerMove={event => {
          const start = drag.current;
          if (!start) return;
          setRect({...start.rect,
            x: Math.max(0, Math.min(1 - start.rect.w, start.rect.x + (event.clientX - start.x) / start.bounds.width)),
            y: Math.max(0, Math.min(1 - start.rect.h, start.rect.y + (event.clientY - start.y) / start.bounds.height))});
        }} onPointerUp={() => {drag.current = null;}} onPointerCancel={() => {drag.current = null;}}>
        <span className="image-crop-guide" />
      </div>}
    </div>}
    {rect && size && <fieldset disabled={saving}>
      <legend>Frame position and size</legend>
      <label>Zoom <input type="range" min="1" max={Math.max(8, zoom)} step="0.01" value={zoom} onChange={event => {
        const next = Number(event.target.value); const frame = centered(preset, size.width, size.height, next);
        setRect(normalizeRect({...frame, x: Math.max(0, Math.min(1 - frame.w, rect.x + (rect.w - frame.w) / 2)),
          y: Math.max(0, Math.min(1 - frame.h, rect.y + (rect.h - frame.h) / 2))})); setZoom(next);
      }} /></label>
      {['x', 'y'].map(key => <label key={key}>{key === 'x' ? 'Horizontal position' : 'Vertical position'}
        <input type="range" min="0" max={Math.max(0, 1 - rect[key === 'x' ? 'w' : 'h'])} step="0.001" value={rect[key]}
          onChange={event => move(key, Number(event.target.value))} /></label>)}
      {preset === 'free' && ['w', 'h'].map(key => <label key={key}>{key === 'w' ? 'Width' : 'Height'}
        <input type="range" min="0.01" max="1" step="0.001" value={rect[key]} onChange={event => {
          const value = Number(event.target.value); const axis = key === 'w' ? 'x' : 'y';
          setRect({...rect, [key]: value, [axis]: Math.min(rect[axis], 1 - value)});
        }} /></label>)}
      <p>Unsaved frame: {JSON.stringify(rect)}</p>
    </fieldset>}
    <div className="image-crop-actions">
      <button type="button" disabled={saving} onClick={onCancel}>Cancel</button>
      <button type="button" disabled={saving || !size} onClick={() => reset()}>Reset framing</button>
      <button type="button" disabled={saving} onClick={() => setAttempt(value => value + 1)}>Reload original</button>
      <button type="button" disabled={saving || loading || !size || !rect} onClick={save}>{saving ? 'Saving copy…' : 'Save new copy'}</button>
    </div>
  </Modal>;
}
